/**
 * The password policy, ticking off as you type.
 *
 * Live rather than on submit, and that is the whole reason it exists. A person
 * choosing a password under five rules who is told about them one refusal at a
 * time — too short, then no capital, then no digit — gives up somewhere around
 * the third attempt. Showing all five from the start turns guessing into
 * following instructions.
 *
 * The rules come from `@parinaam-vms/shared`, which is the same list the API
 * enforces. A checklist that drew its own rules would eventually show five
 * green ticks against a request the server refuses, which is worse than showing
 * nothing.
 *
 * ---------------------------------------------------------------------------
 * Announcing it without narrating it
 * ---------------------------------------------------------------------------
 * The list is `aria-live="polite"`, so a screen-reader user hears rules being
 * satisfied instead of discovering them on a failed submit. Each item carries
 * its state as a word — "met" / "not met" — because a tick mark and a colour
 * are both invisible to them, and colour alone is not a state to anybody who
 * cannot distinguish it.
 */

import { checkPassword } from '@parinaam-vms/shared';
import styles from './PasswordRules.module.css';

interface Props {
  /** What has been typed so far. Empty is the normal starting state. */
  value: string;
  /** Announced to assistive technology as a heading for the list. */
  label?: string;
}

export function PasswordRules({ value, label = 'Your password needs' }: Props) {
  const rules = checkPassword(value);

  return (
    <div className={styles.wrapper}>
      <p className={styles.legend}>{label}</p>
      <ul className={styles.list} aria-live="polite">
        {rules.map((rule) => (
          <li
            key={rule.id}
            className={`${styles.rule} ${rule.met ? styles.met : styles.unmet}`}
            data-met={rule.met}
          >
            {/*
              The mark is decoration: it repeats what the text beside it already
              says, so it is hidden rather than read out as "check" or, worse,
              as nothing at all.
            */}
            <span className={styles.mark} aria-hidden="true">
              {rule.met ? '✓' : '○'}
            </span>
            <span>{rule.label}</span>
            <span className={styles.visuallyHidden}>{rule.met ? ' — met' : ' — not met'}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
